import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppAuth } from 'src/context/use-app-auth';
import {
  fetchLibreChatConversations,
  getLibreChatBaseUrl,
  LibreChatConversation,
  LibreChatMessage,
  deleteLibreChatConversation,
  sendLibreChatPrompt,
  streamLibreChatPrompt,
} from '../../../../api/services/chat-service';
import { useLibreChatMessages } from '../hooks/use-chat-messages';

type ChatContextState = {
  baseUrl: string;
  conversations: LibreChatConversation[];
  activeConversationId: string | null;
  messages: LibreChatMessage[];
  loadingConversations: boolean;
  loadingMessages: boolean;
  sending: boolean;
  error: string | null;
  selectConversation: (id: string) => void;
  startNewConversation: () => void;
  refreshConversations: () => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
};

const ChatContext = createContext<ChatContextState | undefined>(undefined);

export const useAiAssistantChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useAiAssistantChat must be used within ChatProvider');
  }
  return ctx;
};

const DEFAULT_ERROR = 'Unable to reach LibreChat. Please try again.';

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, location } = useAppAuth();
  const baseUrl = useMemo(() => getLibreChatBaseUrl(), []);

  const [conversations, setConversations] = useState<LibreChatConversation[]>(
    [],
  );
  const [messages, setMessages] = useState<LibreChatMessage[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    messages: fetchedMessages,
    loading: fetchedLoading,
    error: fetchedError,
  } = useLibreChatMessages(activeConversationId);

  const refreshConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const data = await fetchLibreChatConversations();
      setConversations(data);
    } catch (_e) {
      setError(DEFAULT_ERROR);
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  const selectConversation = useCallback((id: string) => {
    setActiveConversationId(id);
  }, []);

  useEffect(() => {
    setLoadingMessages(fetchedLoading);
    if (fetchedError) {
      setError(DEFAULT_ERROR);
    }
    if (fetchedMessages) {
      setMessages(fetchedMessages);
    }
  }, [fetchedError, fetchedLoading, fetchedMessages]);

  const startNewConversation = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setError(null);
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      const now = new Date().toISOString();
      setError(null);
      setSending(true);

      const userMessage: LibreChatMessage = {
        id: `user-${now}`,
        role: 'user',
        content: trimmed,
        createdAt: now,
      };

      const assistantId = `assistant-${now}`;
      const assistantMessage: LibreChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '…',
        createdAt: now,
      };

      setMessages(prev => [...prev, userMessage, assistantMessage]);
      const historyForRequest = [...messages, userMessage];

      const userContext = {
        id: user?.key,
        name: user?.name,
        email: user?.email ?? undefined,
        location: location?.key,
      };

      try {
        await streamLibreChatPrompt(
          {
            conversationId: activeConversationId ?? undefined,
            message: trimmed,
            history: historyForRequest,
            user: userContext,
          },
          {
            onToken: chunk =>
              setMessages(prev =>
                prev.map(m =>
                  m.id === assistantId
                    ? { ...m, content: `${m.content || ''}${chunk}` }
                    : m,
                ),
              ),
            onConversationId: cid =>
              setActiveConversationId(
                typeof cid === 'string' ? cid : activeConversationId,
              ),
            onDone: async () => {
              await refreshConversations();
            },
            onError: async () => {
              try {
                const resp = await sendLibreChatPrompt({
                  conversationId: activeConversationId ?? undefined,
                  message: trimmed,
                  history: historyForRequest,
                  user: userContext,
                });
                if (resp.messages && resp.messages.length > 0) {
                  setMessages(resp.messages);
                } else if (resp.text) {
                  setMessages(prev =>
                    prev.map(m =>
                      m.id === assistantId
                        ? { ...m, content: resp.text || '' }
                        : m,
                    ),
                  );
                }
                if (resp.conversationId) {
                  setActiveConversationId(resp.conversationId);
                }
              } catch (_fallbackErr) {
                setError(DEFAULT_ERROR);
                setMessages(prev =>
                  prev.map(m =>
                    m.id === assistantId ? { ...m, content: DEFAULT_ERROR } : m,
                  ),
                );
              }
            },
          },
        );
      } catch (_err) {
        setError(DEFAULT_ERROR);
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantId ? { ...m, content: DEFAULT_ERROR } : m,
          ),
        );
      } finally {
        setSending(false);
      }
    },
    [
      activeConversationId,
      location?.key,
      messages,
      refreshConversations,
      user?.email,
      user?.key,
      user?.name,
    ],
  );

  const value = {
    baseUrl,
    conversations,
    activeConversationId,
    messages,
    loadingConversations,
    loadingMessages,
    sending,
    error,
    selectConversation,
    startNewConversation,
    refreshConversations,
    deleteConversation: deleteLibreChatConversation,
    sendMessage,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
